/**
 * Centralized validation utilities - Optimized for performance
 * Provides efficient, real-time validation for declaration fields
 * Includes both immediate error removal and lightweight field validation
 */

import { getFieldId } from './fieldIdUtils';
import { getDeclarationKind } from './declarationKindUtils';

/**
 * Check if a value is empty (null, undefined, or empty string)
 * Allows 0, false, and other falsy-but-valid values
 */
export const isEmpty = (value) => {
  return value === null || value === undefined || value === '';
};

/**
 * Extract numeric index from error title (e.g., "Item #1" -> 0)
 * Cached for performance
 */
const indexCache = new Map();
const extractIndex = (title) => {
  if (!title) return null;
  if (indexCache.has(title)) return indexCache.get(title);
  const match = title.match(/\d+/);
  const result = match ? parseInt(match[0], 10) - 1 : null;
  if (result !== null) indexCache.set(title, result);
  return result;
};

/**
 * Optimized: Add or remove validation error for a specific field
 * Handles both adding errors (when field becomes empty) and removing errors (when field is filled)
 */
export const removeFieldErrorImmediately = (
  currentErrors,
  declarationIndex,
  field,
  value,
  itemIndex = null,
  docIndex = null,
  embalajeIndex = null,
  marcaIndex = null,
  isSpanish = true,
  declarationKind = null
) => {
  const declNum = declarationIndex + 1;
  const targetFieldId = field === 'marcas_marca' && marcaIndex !== null
    ? getFieldId(`packages_${field}`, declarationIndex, itemIndex, docIndex, embalajeIndex, marcaIndex)
    : getFieldId(field, declarationIndex, itemIndex, docIndex, embalajeIndex);
  
  const isFieldEmpty = isEmpty(value);
  
  // If value is empty, check if field is required and add error
  if (isFieldEmpty) {
    // Determine field type
    let fieldType = 'declaration';
    if (itemIndex !== null && docIndex === null && embalajeIndex === null) {
      fieldType = 'item';
    } else if (docIndex !== null && itemIndex !== null) {
      fieldType = 'document';
    } else if (embalajeIndex !== null && itemIndex !== null) {
      fieldType = marcaIndex !== null ? 'package_marca' : 'package';
    }
    
    const fieldConfig = REQUIRED_FIELDS_CONFIG.find(f => f.path === field && f.type === fieldType);
    if (!fieldConfig) {
      return null; // Not a required field
    }
    // Kind-gated declaration fields (e.g. declaracion_1 is required for import
    // only) should not be flagged on declarations of the other kind.
    if (fieldConfig.kinds && declarationKind && !fieldConfig.kinds.includes(declarationKind)) {
      return null;
    }
    
    const existingDecl = currentErrors?.find(d => d.declarationNumber === declNum);
    
    // Check if error already exists (check the specific error array for this field type)
    let hasError = false;
    if (fieldType === 'declaration') {
      hasError = existingDecl?.declarationErrors?.some(e => e.field === targetFieldId);
    } else if (fieldType === 'item') {
      const itemTitle = `Item #${itemIndex + 1}`;
      hasError = existingDecl?.itemErrors
        ?.find(item => item.title === itemTitle)
        ?.errors?.some(e => e.field === targetFieldId);
    } else if (fieldType === 'document') {
      const itemTitle = `Item #${itemIndex + 1}`;
      const docTitle = `Document #${docIndex + 1}`;
      hasError = existingDecl?.documentErrors
        ?.find(doc => doc.itemTitle === itemTitle && doc.documentTitle === docTitle)
        ?.errors?.some(e => e.field === targetFieldId);
    } else if (fieldType === 'package' || fieldType === 'package_marca') {
      const itemTitle = `Item #${itemIndex + 1}`;
      const pkgTitle = `Package #${embalajeIndex + 1}`;
      hasError = existingDecl?.packageErrors
        ?.find(pkg => pkg.itemTitle === itemTitle && pkg.packageTitle === pkgTitle)
        ?.errors?.some(e => e.field === targetFieldId);
    }
    
    if (hasError) return null; // Error already exists
    
    const lang = isSpanish ? 'es' : 'en';
    const newError = { field: targetFieldId, error: ERROR_MESSAGES[fieldConfig.errorKey][lang] };
    
    if (!existingDecl) {
      // New declaration error entry
      const newDecl = {
        declarationNumber: declNum,
        declarationErrors: fieldType === 'declaration' ? [newError] : [],
        itemErrors: [],
        packageErrors: [],
        documentErrors: []
      };
      
      if (fieldType === 'item') {
        newDecl.itemErrors = [{
          title: `Item #${itemIndex + 1}`,
          errors: [newError]
        }];
      } else if (fieldType === 'document') {
        newDecl.documentErrors = [{
          itemTitle: `Item #${itemIndex + 1}`,
          documentTitle: `Document #${docIndex + 1}`,
          errors: [newError]
        }];
      } else if (fieldType === 'package' || fieldType === 'package_marca') {
        newDecl.packageErrors = [{
          itemTitle: `Item #${itemIndex + 1}`,
          packageTitle: `Package #${embalajeIndex + 1}`,
          errors: [newError]
        }];
      }
      
      return [...(currentErrors || []), newDecl];
    }
    
    // Update existing declaration
    const updated = { ...existingDecl };
    
    if (fieldType === 'declaration') {
      updated.declarationErrors = [...(existingDecl.declarationErrors || []), newError];
    } else if (fieldType === 'item') {
      const itemTitle = `Item #${itemIndex + 1}`;
      const existingItem = existingDecl.itemErrors?.find(item => item.title === itemTitle);
      if (existingItem) {
        updated.itemErrors = existingDecl.itemErrors.map(item =>
          item.title === itemTitle
            ? { ...item, errors: [...(item.errors || []), newError] }
            : item
        );
      } else {
        updated.itemErrors = [...(existingDecl.itemErrors || []), {
          title: itemTitle,
          errors: [newError]
        }];
      }
    } else if (fieldType === 'document') {
      const itemTitle = `Item #${itemIndex + 1}`;
      const docTitle = `Document #${docIndex + 1}`;
      const existingDoc = existingDecl.documentErrors?.find(doc =>
        doc.itemTitle === itemTitle && doc.documentTitle === docTitle
      );
      if (existingDoc) {
        updated.documentErrors = existingDecl.documentErrors.map(doc =>
          doc.itemTitle === itemTitle && doc.documentTitle === docTitle
            ? { ...doc, errors: [...(doc.errors || []), newError] }
            : doc
        );
      } else {
        updated.documentErrors = [...(existingDecl.documentErrors || []), {
          itemTitle,
          documentTitle: docTitle,
          errors: [newError]
        }];
      }
    } else if (fieldType === 'package' || fieldType === 'package_marca') {
      const itemTitle = `Item #${itemIndex + 1}`;
      const pkgTitle = `Package #${embalajeIndex + 1}`;
      const existingPkg = existingDecl.packageErrors?.find(pkg =>
        pkg.itemTitle === itemTitle && pkg.packageTitle === pkgTitle
      );
      if (existingPkg) {
        updated.packageErrors = existingDecl.packageErrors.map(pkg =>
          pkg.itemTitle === itemTitle && pkg.packageTitle === pkgTitle
            ? { ...pkg, errors: [...(pkg.errors || []), newError] }
            : pkg
        );
      } else {
        updated.packageErrors = [...(existingDecl.packageErrors || []), {
          itemTitle,
          packageTitle: pkgTitle,
          errors: [newError]
        }];
      }
    }
    
    return currentErrors.map(d => d.declarationNumber === declNum ? updated : d);
  }
  
  // Value is not empty - remove error if exists
  if (!currentErrors?.length) {
    return null;
  }
  
  let hasChanges = false;
  const updatedErrors = [];
  
  // Single pass through declarations
  for (const decl of currentErrors) {
    if (decl.declarationNumber !== declNum) {
      updatedErrors.push(decl);
      continue;
    }
    
    const updated = { declarationNumber: decl.declarationNumber };
    let hasRemainingErrors = false;
    
    // Declaration errors - simple filter (check exact match)
    if (decl.declarationErrors?.length) {
      const beforeLength = decl.declarationErrors.length;
      const filtered = decl.declarationErrors.filter(e => e.field !== targetFieldId);
      if (filtered.length !== beforeLength) {
        updated.declarationErrors = filtered;
        hasChanges = true;
      } else {
        updated.declarationErrors = decl.declarationErrors;
      }
      if (filtered.length) {
        hasRemainingErrors = true;
      }
    }
    
    // Item errors - only if itemIndex provided and no doc/embalaje
    if (itemIndex !== null && docIndex === null && embalajeIndex === null) {
      if (decl.itemErrors?.length) {
        const filtered = [];
        for (const itemError of decl.itemErrors) {
          const itemNum = extractIndex(itemError.title);
          if (itemNum === itemIndex) {
            const filteredErrors = (itemError.errors || []).filter(e => e.field !== targetFieldId);
            if (filteredErrors.length !== (itemError.errors || []).length) {
              hasChanges = true;
              if (filteredErrors.length) {
                filtered.push({ ...itemError, errors: filteredErrors });
                hasRemainingErrors = true;
              }
              continue;
            }
          }
          filtered.push(itemError);
          if (itemError.errors?.length) hasRemainingErrors = true;
        }
        updated.itemErrors = filtered;
      }
    } else if (decl.itemErrors?.length) {
      updated.itemErrors = decl.itemErrors;
      hasRemainingErrors = true;
    }
    
    // Document errors - only if both itemIndex and docIndex provided
    if (docIndex !== null && itemIndex !== null) {
      if (decl.documentErrors?.length) {
        const filtered = [];
        for (const docError of decl.documentErrors) {
          const itemNum = extractIndex(docError.itemTitle);
          const docNum = extractIndex(docError.documentTitle);
          if (itemNum === itemIndex && docNum === docIndex) {
            const filteredErrors = (docError.errors || []).filter(e => e.field !== targetFieldId);
            if (filteredErrors.length !== (docError.errors || []).length) {
              hasChanges = true;
              if (filteredErrors.length) {
                filtered.push({ ...docError, errors: filteredErrors });
                hasRemainingErrors = true;
              }
              continue;
            }
          }
          filtered.push(docError);
          if (docError.errors?.length) hasRemainingErrors = true;
        }
        updated.documentErrors = filtered;
      }
    } else if (decl.documentErrors?.length) {
      updated.documentErrors = decl.documentErrors;
      hasRemainingErrors = true;
    }
    
    // Package errors - only if both itemIndex and embalajeIndex provided
    if (embalajeIndex !== null && itemIndex !== null) {
      if (decl.packageErrors?.length) {
        const filtered = [];
        for (const pkgError of decl.packageErrors) {
          const itemNum = extractIndex(pkgError.itemTitle);
          const pkgNum = extractIndex(pkgError.packageTitle);
          if (itemNum === itemIndex && pkgNum === embalajeIndex) {
            const filteredErrors = (pkgError.errors || []).filter(e => e.field !== targetFieldId);
            if (filteredErrors.length !== (pkgError.errors || []).length) {
              hasChanges = true;
              if (filteredErrors.length) {
                filtered.push({ ...pkgError, errors: filteredErrors });
                hasRemainingErrors = true;
              }
              continue;
            }
          }
          filtered.push(pkgError);
          if (pkgError.errors?.length) hasRemainingErrors = true;
        }
        updated.packageErrors = filtered;
      }
    } else if (decl.packageErrors?.length) {
      updated.packageErrors = decl.packageErrors;
      hasRemainingErrors = true;
    }
    
    // Only add declaration if it has remaining errors
    if (hasRemainingErrors) {
      updated.declarationNumber = decl.declarationNumber;
      updatedErrors.push(updated);
    }
  }
  
  // Return null if no changes (but allow empty array if all errors were cleared)
  if (!hasChanges) {
    return null;
  }
  
  // Return empty array if all errors were cleared (this is a valid change)
  return updatedErrors;
};

/**
 * Required field configuration - defines what needs validation
 * Format: { path, type, errorKey, validator?, conditional? }
 */
const REQUIRED_FIELDS_CONFIG = [
  // Declaration-level fields (expediente is auto-generated — not required for save/validation)
  // `kinds` restricts a field to specific declaration kinds; absent = both.
  { path: 'tipo', type: 'declaration', errorKey: 'tipo' },
  { path: 'clave_declarante', type: 'declaration', errorKey: 'clave_declarante', kinds: ['export'] },
  { path: 'contacto_telefono', type: 'declaration', errorKey: 'contacto_telefono', kinds: ['export'] },
  { path: 'flag_datos_seguridad', type: 'declaration', errorKey: 'flag_datos_seguridad', kinds: ['export'],
    validator: (val) => val === null || val === undefined || Array.isArray(val) },
  // Import-only declaration fields (required by the H1 import schema)
  { path: 'declaracion_1', type: 'declaration', errorKey: 'declaracion_1', kinds: ['import'] },
  { path: 'declaracion_2', type: 'declaration', errorKey: 'declaracion_2', kinds: ['import'] },
  { path: 'aduana_despacho', type: 'declaration', errorKey: 'aduana_despacho', kinds: ['import'] },
  { path: 'pais_exportacion', type: 'declaration', errorKey: 'pais_exportacion', kinds: ['import'] },

  // Item-level fields
  { path: 'descripcion_mercancia', type: 'item', errorKey: 'descripcion_mercancia' },
  { path: 'codigo_nc', type: 'item', errorKey: 'codigo_nc' },
  { path: 'masa_bruta', type: 'item', errorKey: 'masa_bruta', 
    validator: (val) => val === null || val === undefined || val === '' },
  { path: 'masa_neta', type: 'item', errorKey: 'masa_neta', 
    validator: (val) => val === null || val === undefined || val === '' },
  { path: 'pais_origen', type: 'item', errorKey: 'pais_origen' },
  
  // Package-level fields
  { path: 'tipo', type: 'package', errorKey: 'packages_tipo' },
  { path: 'es_interno_o_externo', type: 'package', errorKey: 'packages_es_interno_o_externo' },
  { path: 'cantidad', type: 'package', errorKey: 'packages_cantidad', 
    validator: (val) => val === null || val === undefined || val === '',
    conditional: (embalaje) => {
      // Only validate if package has required fields and quantity data
      const hasRequired = !isEmpty(embalaje.tipo) && !isEmpty(embalaje.es_interno_o_externo);
      const hasQuantityData = (embalaje.cantidad_restante != null && embalaje.cantidad_restante !== '') ||
                             (embalaje.piezas != null && embalaje.piezas !== '');
      return hasRequired && hasQuantityData;
    }},
  { path: 'marca', type: 'package_marca', errorKey: 'packages_marcas_marca' },
  
  // Document-level fields
  { path: 'codigo_documento', type: 'document', errorKey: 'documents_codigo_documento' },
  { path: 'tipo', type: 'document', errorKey: 'documents_tipo' },
];

/**
 * Error messages map
 */
const ERROR_MESSAGES = {
  expediente: { en: 'Declaration', es: 'Expediente' },
  tipo: { en: 'Type', es: 'Tipo' },
  clave_declarante: { en: 'Declarant Key', es: 'Clave Declarante' },
  contacto_telefono: { en: 'Phone Number', es: 'Teléfono' },
  flag_datos_seguridad: { en: 'Security Data Flag', es: 'Datos de Seguridad' },
  declaracion_1: { en: 'Declaration 1', es: 'Declaración 1' },
  declaracion_2: { en: 'Declaration 2', es: 'Declaración 2' },
  aduana_despacho: { en: 'Clearance Customs Office', es: 'Aduana de Despacho' },
  pais_exportacion: { en: 'Country of Export', es: 'País de Exportación' },
  descripcion_mercancia: { en: 'Goods Description', es: 'Descripción de Mercancía' },
  codigo_nc: { en: 'NC Code', es: 'Código NC' },
  masa_bruta: { en: 'Gross Weight', es: 'Masa Bruta' },
  masa_neta: { en: 'Net Weight', es: 'Masa Neta' },
  pais_origen: { en: 'Country of Origin', es: 'País de Origen' },
  'packages_tipo': { en: 'Type', es: 'Tipo' },
  'packages_es_interno_o_externo': { en: 'Internal or External', es: 'Es Interno o Externo' },
  'packages_cantidad': { en: 'Quantity', es: 'Cantidad' },
  'packages_marcas_marca': { en: 'Mark', es: 'Marca' },
  'documents_codigo_documento': { en: 'Document Code', es: 'Código de Documento' },
  'documents_tipo': { en: 'Type', es: 'Tipo' },
};

/**
 * Lightweight validation - only checks required fields
 * Separate from main data flow to prevent re-renders
 * Optimized for performance during typing
 * @param {Array} data - Declaration data (from ref, not Redux to avoid re-renders)
 * @param {boolean} isSpanish - Language flag
 * @returns {Array} Validation errors (minimal structure)
 */
export const validateRequiredFieldsOnly = (data, isSpanish = true) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  const errors = [];
  const lang = isSpanish ? 'es' : 'en';

  // Only iterate through declarations that exist
  data.forEach((declaration, declarationIndex) => {
    if (!declaration || typeof declaration !== 'object') return;

    const declarationErrors = [];
    const itemErrors = [];
    const packageErrors = [];
    const documentErrors = [];

    // Check declaration-level required fields only (kind-aware: import and
    // export declarations require different declaration-level fields).
    const declarationKind = getDeclarationKind(declaration);
    REQUIRED_FIELDS_CONFIG
      .filter(field => field.type === 'declaration')
      .filter(field => !field.kinds || field.kinds.includes(declarationKind))
      .forEach(field => {
        const value = declaration[field.path];
        const isValid = field.validator 
          ? !field.validator(value)
          : !isEmpty(value);
        
        if (!isValid) {
          const fieldId = getFieldId(field.path, declarationIndex);
          declarationErrors.push({
            field: fieldId,
            error: ERROR_MESSAGES[field.errorKey][lang]
          });
        }
      });

    // Check item-level fields only if partidas exist
    if (declaration.partidas && Array.isArray(declaration.partidas)) {
      declaration.partidas.forEach((item, itemIndex) => {
        if (!item || typeof item !== 'object') return;

        const currentItemErrors = [];

        // Check item-level required fields only
        REQUIRED_FIELDS_CONFIG
          .filter(field => field.type === 'item')
          .forEach(field => {
            const value = item[field.path];
            const isValid = field.validator 
              ? !field.validator(value)
              : !isEmpty(value);
            
            if (!isValid) {
              const fieldId = getFieldId(field.path, declarationIndex, itemIndex);
              currentItemErrors.push({
                field: fieldId,
                error: ERROR_MESSAGES[field.errorKey][lang]
              });
            }
          });

        // Check package fields only if embalajes exist
        if (item.embalajes && Array.isArray(item.embalajes)) {
          item.embalajes.forEach((embalaje, embalajeIndex) => {
            if (!embalaje || typeof embalaje !== 'object') return;

            const currentPackageErrors = [];

            // Check package-level required fields only
            REQUIRED_FIELDS_CONFIG
              .filter(field => field.type === 'package')
              .forEach(field => {
                // Check conditional validation
                if (field.conditional && !field.conditional(embalaje)) {
                  return; // Skip this field
                }

                const value = embalaje[field.path];
                const isValid = field.validator 
                  ? !field.validator(value)
                  : !isEmpty(value);
                
                if (!isValid) {
                  const fieldId = getFieldId(
                    `packages_${field.path}`,
                    declarationIndex,
                    itemIndex,
                    null,
                    embalajeIndex
                  );
                  currentPackageErrors.push({
                    field: fieldId,
                    error: ERROR_MESSAGES[field.errorKey][lang]
                  });
                }
              });

            // Check marca fields only if marcas exist
            if (embalaje.marcas && Array.isArray(embalaje.marcas)) {
              embalaje.marcas.forEach((marca, marcaIndex) => {
                if (!marca || typeof marca !== 'object') return;

                REQUIRED_FIELDS_CONFIG
                  .filter(field => field.type === 'package_marca')
                  .forEach(field => {
                    const value = marca[field.path];
                    if (isEmpty(value)) {
                      const fieldId = getFieldId(
                        `packages_marcas_${field.path}`,
                        declarationIndex,
                        itemIndex,
                        null,
                        embalajeIndex,
                        marcaIndex
                      );
                      currentPackageErrors.push({
                        field: fieldId,
                        error: ERROR_MESSAGES[field.errorKey][lang]
                      });
                    }
                  });
              });
            }

            if (currentPackageErrors.length > 0) {
              packageErrors.push({
                itemTitle: isSpanish ? `Partida #${itemIndex + 1}` : `Item #${itemIndex + 1}`,
                packageTitle: isSpanish ? `Embalaje #${embalajeIndex + 1}` : `Package #${embalajeIndex + 1}`,
                errors: currentPackageErrors
              });
            }
          });
        }

        // Check document fields only if documentos exist
        if (item.documentos && Array.isArray(item.documentos)) {
          item.documentos.forEach((documento, docIndex) => {
            if (!documento || typeof documento !== 'object') return;

            const currentDocumentErrors = [];

            // Check document-level required fields only
            REQUIRED_FIELDS_CONFIG
              .filter(field => field.type === 'document')
              .forEach(field => {
                const value = documento[field.path];
                if (isEmpty(value)) {
                  const fieldId = getFieldId(
                    `documents_${field.path}`,
                    declarationIndex,
                    itemIndex,
                    docIndex
                  );
                  currentDocumentErrors.push({
                    field: fieldId,
                    error: ERROR_MESSAGES[field.errorKey][lang]
                  });
                }
              });

            if (currentDocumentErrors.length > 0) {
              documentErrors.push({
                itemTitle: isSpanish ? `Partida #${itemIndex + 1}` : `Item #${itemIndex + 1}`,
                documentTitle: isSpanish ? `Documento #${docIndex + 1}` : `Document #${docIndex + 1}`,
                errors: currentDocumentErrors
              });
            }
          });
        }

        if (currentItemErrors.length > 0) {
          itemErrors.push({
            title: isSpanish ? `Partida #${itemIndex + 1}` : `Item #${itemIndex + 1}`,
            errors: currentItemErrors
          });
        }
      });
    }

    // Only add declaration if it has errors
    if (declarationErrors.length > 0 || itemErrors.length > 0 || packageErrors.length > 0 || documentErrors.length > 0) {
      errors.push({
        declarationNumber: declarationIndex + 1,
        declarationErrors,
        itemErrors,
        packageErrors,
        documentErrors
      });
    }
  });

  return errors;
};

/**
 * Quick check for a single field - used during typing
 * @param {Object} data - Single declaration/item/package/document object
 * @param {string} fieldPath - Field path (e.g., 'contacto_telefono', 'descripcion_mercancia')
 * @param {string} fieldType - Type: 'declaration', 'item', 'package', 'document', 'package_marca'
 * @returns {boolean} True if field is valid (has value)
 */
export const isFieldValid = (data, fieldPath, fieldType = 'declaration') => {
  if (!data || typeof data !== 'object') return false;

  const fieldConfig = REQUIRED_FIELDS_CONFIG.find(
    f => f.path === fieldPath && f.type === fieldType
  );

  if (!fieldConfig) return true; // Not a required field, consider valid

  const value = data[fieldPath];
  
  if (fieldConfig.validator) {
    return !fieldConfig.validator(value);
  }

  return !isEmpty(value);
};
